import React, { createContext, useContext, useMemo } from 'react';

const LOG_LEVELS = {
    ERROR: 0,
    WARN: 1,
    INFO: 2,
    DEBUG: 3
};

const LOG_STYLES = {
    ERROR: { emoji: '🔴', method: 'error' },
    WARN: { emoji: '🟠', method: 'warn' },
    INFO: { emoji: '🔵', method: 'log' },
    DEBUG: { emoji: '🟣', method: 'debug' }
};

const MAX_LENGTH = 1000;
// Increased dedup window and added level-specific settings
const DEDUP_SETTINGS = {
    ERROR: { windowMs: 1000, batchSize: 1 },    // Show all errors
    WARN: { windowMs: 2000, batchSize: 5 },     // Group warnings in 2s windows
    INFO: { windowMs: 5000, batchSize: 10 },    // Group info in 5s windows
    DEBUG: { windowMs: 10000, batchSize: 50 }   // Heavily group debug logs in 10s windows
};

const LoggerContext = createContext(null);

// Message deduplication cache with automatic cleanup
const messageCache = new Map();
// Cleanup old cache entries every minute
const cleanupInterval = setInterval(() => {
    const now = Date.now();
    for (const [key, value] of messageCache.entries()) {
        const settings = DEDUP_SETTINGS[value.level] || DEDUP_SETTINGS.INFO;
        if (now - value.timestamp > settings.windowMs) {
            messageCache.delete(key);
        }
    }
}, 60000);
if (typeof cleanupInterval === 'object') {
    if (typeof cleanupInterval.unref !== 'function') {
        throw new Error('LoggerProvider: Node cleanup interval must support unref');
    }
    cleanupInterval.unref();
}

// Track the last group time and group state
let lastGroupTime = null;
let isGroupOpen = false;
const GROUP_INTERVAL_MS = 1000; // 1 second

/**
 * Gets the group timestamp (rounded to nearest 5 second interval)
 */
function getGroupTime(timestamp) {
    const time = new Date(timestamp).getTime();
    return Math.floor(time / GROUP_INTERVAL_MS) * GROUP_INTERVAL_MS;
}

/**
 * Formats the group header timestamp range
 */
function formatGroupHeader(groupTime) {
    const start = new Date(groupTime).toISOString();
    const end = new Date(groupTime + GROUP_INTERVAL_MS).toISOString();
    return `Logs from ${start} to ${end}`;
}

/**
 * Truncates a string to a maximum length, adding an ellipsis if truncated
 * 
 * @param {string} str - The string to truncate
 * @param {number} maxLength - Maximum length of the string
 * @returns {string} Truncated string
 */
function truncateString(str, maxLength) {
    if (!str || str.length <= maxLength) return str;
    return str.substring(0, maxLength - 3) + '...';
}

/**
 * Checks if a message should be deduplicated
 */
function shouldDeduplicate(key, data, level) {
    const now = Date.now();
    const settings = DEDUP_SETTINGS[level] || DEDUP_SETTINGS.INFO;
    const cached = messageCache.get(key);
    
    if (!cached) {
        messageCache.set(key, {
            timestamp: now,
            lastLogTime: now,
            count: 1,
            level
        });
        return false;
    }

    // If outside dedup window, reset cache and treat as new
    if (now - cached.timestamp > settings.windowMs) {
        messageCache.set(key, {
            timestamp: now,
            lastLogTime: now,
            count: 1,
            level
        });
        return false;
    }

    // Update cache
    cached.count++;
    cached.lastLogTime = now;
    messageCache.set(key, cached);

    // Only log on batch boundaries based on level settings
    return cached.count % settings.batchSize !== 1;
}

/**
 * Normalizes the (methodName, data) arguments of the logging methods.
 *
 * The public signature is (message, methodName, data), but call sites use three
 * conventions: the correct (message, methodNameString, dataObject); the common
 * (message, dataObject) where the data lands in the methodName slot; and the
 * legacy (message, dataObject, methodNameString) (e.g. ErrorBoundary). A non-string
 * in the methodName slot was previously rendered as ".[object Object]" in the log
 * prefix while the real data was dropped. This keeps the methodName a string (or
 * null) and routes any object to the data slot so the payload always surfaces.
 *
 * @param {*} methodName - Value passed in the methodName slot.
 * @param {*} data - Value passed in the data slot.
 * @returns {{ methodName: (string|null|undefined), data: * }}
 */
function normalizeLogArgs(methodName, data) {
    if (methodName === null || methodName === undefined || typeof methodName === 'string') {
        return { methodName, data };
    }
    // methodName is a non-string (object/array) → it is actually the data payload.
    if (typeof data === 'string') {
        // Legacy ordering: (message, dataObject, methodNameString).
        return { methodName: data, data: methodName };
    }
    return { methodName: null, data: methodName };
}

/**
 * Creates a logger instance for React components.
 *
 * @param {string} componentName - The name of the component or context
 * @param {Object} [config] - Optional configuration
 * @param {boolean} [config.enabled=true] - Whether logging is enabled
 * @param {string} [config.minLogLevel='INFO'] - Minimum log level to display
 * @returns {Object} Logger instance with logging methods
 */
function createLogger(componentName, config = {}) {
    const state = {
        componentName,
        enabled: config.enabled !== undefined ? config.enabled : true,
        minLogLevel: LOG_LEVELS[config.minLogLevel] || LOG_LEVELS.DEBUG,
        deduplicate: config.deduplicate !== undefined ? config.deduplicate : true
    };

    function shouldLog(level) {
        if (!state.enabled) return false;
        return LOG_LEVELS[level] <= state.minLogLevel;
    }

    function formatMessage(message, level, methodName = null) {
        const timestamp = new Date().toISOString();
        const { emoji } = LOG_STYLES[level];
        const methodPrefix = methodName ? `.${methodName}` : '';
        if (typeof message === 'object') {
            return truncateString(`${emoji} [${timestamp}] ${state.componentName}${methodPrefix}: ${JSON.stringify(message, null, 2)}`, MAX_LENGTH);
        }
        return truncateString(`${emoji} [${timestamp}] ${state.componentName}${methodPrefix}: ${message}`, MAX_LENGTH);
    }

    function formatData(data) {
        if (data === null || data === undefined) return '';
        try {
            if (data instanceof Error) {
                return data;
            }
            const formatted = typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data);
            return truncateString(formatted, MAX_LENGTH);
        } catch (e) {
            return '[Circular or Invalid Object]';
        }
    }

    /**
     * Creates a new console group with appropriate collapsed state
     */
    function createGroup(level, groupTime) {
        if (isGroupOpen) {
            console.groupEnd();
        }
        // Use groupCollapsed for DEBUG level, regular group for others
        if (level === 'DEBUG') {
            console.groupCollapsed(formatGroupHeader(groupTime));
        } else {
            console.group(formatGroupHeader(groupTime));
        }
        isGroupOpen = true;
        lastGroupTime = groupTime;
    }

    function logWithDeduplication(level, message, methodName = null, data = null) {
        if (!shouldLog(level)) return;

        const key = `${state.componentName}:${methodName}:${message}`;
        const now = new Date();
        const currentGroupTime = getGroupTime(now);
        
        if (state.deduplicate && shouldDeduplicate(key, data, level)) {
            const cached = messageCache.get(key);
            const settings = DEDUP_SETTINGS[level];
            
            // Only show summary on batch boundaries
            if (cached.count % settings.batchSize === 0) {
                const formattedMessage = formatMessage(
                    `${message} (${cached.count} similar messages in last ${settings.windowMs/1000}s)`, 
                    level, 
                    methodName
                );

                // Handle grouping
                if (currentGroupTime !== lastGroupTime) {
                    createGroup(level, currentGroupTime);
                }
                
                console[LOG_STYLES[level].method](formattedMessage);
                if (data) {
                    console[LOG_STYLES[level].method]('Last data item:', formatData(data));
                }
            }
            return;
        }

        // Handle grouping for non-deduplicated messages
        if (currentGroupTime !== lastGroupTime) {
            createGroup(level, currentGroupTime);
        }

        const formattedMessage = formatMessage(message, level, methodName);
        console[LOG_STYLES[level].method](formattedMessage);
        if (data) {
            console[LOG_STYLES[level].method](formatData(data));
        }
    }

    // Ensure we close any open groups when the logger is destroyed
    function cleanup() {
        if (isGroupOpen) {
            console.groupEnd();
            isGroupOpen = false;
        }
    }

    // Add cleanup to window unload event
    if (typeof window !== 'undefined') {
        window.addEventListener('unload', cleanup);
    }

    return {
        info: (message, methodName = null, data = null) => {
            const n = normalizeLogArgs(methodName, data);
            return logWithDeduplication('INFO', message, n.methodName, n.data);
        },
        warn: (message, methodName = null, data = null) => {
            const n = normalizeLogArgs(methodName, data);
            return logWithDeduplication('WARN', message, n.methodName, n.data);
        },
        error: (message, methodName = null, error = null) => {
            const n = normalizeLogArgs(methodName, error);
            return logWithDeduplication('ERROR', message, n.methodName, n.data);
        },
        debug: (message, methodName = null, data = null) => {
            const n = normalizeLogArgs(methodName, data);
            return logWithDeduplication('DEBUG', message, n.methodName, n.data);
        }
    };
}

/**
 * Provider component that makes logger instance available to any
 * child component that calls useLogger().
 * 
 * @param {Object} props - Provider props
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} [props.config] - Logger configuration
 * @param {boolean} [props.config.enabled] - Whether logging is enabled globally
 * @param {string} [props.config.minLogLevel] - Minimum log level to display globally
 */
const DEFAULT_LOGGER_CONFIG = {};

export function LoggerProvider({ children, config = DEFAULT_LOGGER_CONFIG }) {
    // Memoize the configuration to ensure stability if it's passed as a prop object
    const memoizedConfig = useMemo(() => config, [config]);

    // Memoize the context value
    const contextValue = useMemo(() => ({
        baseConfig: memoizedConfig,
        // Use useCallback to ensure createLogger function reference is stable
        createLogger: (componentName, componentConfig = {}) => {
            const mergedConfig = { ...memoizedConfig, ...componentConfig };
            return createLogger(componentName, mergedConfig);
        }
        // Dependency is the memoized config
    }), [memoizedConfig]);

    return (
        <LoggerContext.Provider value={contextValue}>
            {children}
        </LoggerContext.Provider>
    );
}

/**
 * Hook that returns a logger instance for the component.
 * Must be used within a LoggerProvider component.
 * 
 * @param {string} componentName - The name of the component
 * @returns {Object} Logger instance with logging methods
 * 
 * @example
 * function MyComponent() {
 *   const logger = useLogger('MyComponent');
 *   
 *   useEffect(() => {
 *     logger.info('Component mounted');
 *     return () => logger.info('Component unmounting');
 *   }, [logger]);
 *   
 *   return <div>My Component</div>;
 * }
 */
export function useLogger(componentName) {
    const context = useContext(LoggerContext);
    if (!context) {
        throw new Error('useLogger must be used within a LoggerProvider');
    }

    // Create a memoized logger instance for the component
    const logger = useMemo(() => {
        return context.createLogger(componentName);
    }, [context, componentName]);

    return logger;
}

/**
 * Creates a logger instance for use outside of React component context.
 * Uses a default configuration.
 * 
 * @param {string} moduleName - Name of the module or context requesting the logger.
 * @param {object} [defaultConfig={ minLogLevel: 'INFO' }] - Default config if none provided in ENV.
 * @returns {object} Logger instance.
 */
export function getLogger(moduleName, defaultConfig = { minLogLevel: 'DEBUG' }) {
    // Consider reading default config from environment variables here
    // e.g., const configFromEnv = process.env.REACT_APP_DEFAULT_LOG_CONFIG ? JSON.parse(process.env.REACT_APP_DEFAULT_LOG_CONFIG) : {};
    const config = {
        ...defaultConfig,
        // ...configFromEnv, // Uncomment to allow ENV override
        enabled: true, // Assuming always enabled for global logger, or configure via ENV
    };
    return createLogger(moduleName, config);
}

export default LoggerProvider;
